/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import {DEFAULT_SITE_TITLE, languageAliases} from '../../constants'
import {useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom/cjs/react-router-dom'
import {removeSiteLocaleFromPath} from '../../utils/url'

const Seo = ({
    replaceTitle = false,
    title,
    description,
    noIndex,
    children,
    ...props
}) => {
    const {locale} = useIntl()
    const location = useLocation()
    const [urlBase, setUrlBase] = useState('https://www.rennai.com/')
    const pathname = location?.pathname?.length > 1 ? location?.pathname : ''

    useEffect(() => {
        setUrlBase(window?.location?.origin || 'https://www.rennai.com/')
    }, [])

    const links = useMemo(() => {
        const isEnglish = locale === languageAliases.en.locale
        const newPath = removeSiteLocaleFromPath(pathname)
        const currentLocale = isEnglish
            ? languageAliases.en
            : languageAliases.fr
        const alternativeLocale = !isEnglish
            ? languageAliases.en
            : languageAliases.fr

        const alternate = urlBase?.replace(
            `/${currentLocale.localeName}/`,
            `/${alternativeLocale.localeName}/`
        )

        const canonicalPath =
            isEnglish && newPath.length <= 1
                ? ''
                : `/${currentLocale.localeName}`

        const englishPath =
            newPath.length <= 1 ? '' : `/${languageAliases.en.localeName}`

        return {
            canonical: `${urlBase}${canonicalPath}${newPath}`,
            alternative: `${alternate}/${alternativeLocale.localeName}${newPath}`,
            en: `${alternate}${englishPath}${newPath}`,
            fr: `${alternate}/${languageAliases.fr.localeName}${newPath}`
        }
    }, [pathname, locale, urlBase])

    const fullTitle = title
        ? `${title} | ${DEFAULT_SITE_TITLE}`
        : DEFAULT_SITE_TITLE

    const parsedTitle = useMemo(
        () =>
            title ||
            `Rennaï: Your Self-Care & Beauty Boutique in Montréal, Canada | Rennaï`,
        []
    )

    const parsedDescription = useMemo(
        () =>
            description ||
            `Discover Rennaï, Montréal's top destination for self-care and beauty. Browse our exclusive range of luxury skincare, wellness products, and more for your ultimate self-care experience.`,
        []
    )

    return (
        <Helmet {...props}>
            <title>{fullTitle}</title>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link
                rel="preconnect"
                href="https://fonts.gstatic.com"
                crossOrigin="true"
            />
            <link
                href="https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,100..900;1,100..900&display=swap"
                rel="stylesheet"
            ></link>

            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link
                rel="preconnect"
                href="https://fonts.gstatic.com"
                crossOrigin="true"
            />
            <link
                href="https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap"
                rel="stylesheet"
            ></link>

            <link rel="canonical" href={links.canonical} />
            <link rel="alternate" hrefLang="x-default" href={links.canonical} />
            <link rel="alternate" hrefLang="en" href={links.en} />
            <link rel="alternate" hrefLang="en-ca" href={links.en} />
            <link rel="alternate" hrefLang="fr" href={links.fr} />
            <link rel="alternate" hrefLang="fr-ca" href={links.fr} />

            <title>
                {replaceTitle
                    ? title
                    : 'Rennaï: Your Self-Care & Beauty Boutique in Montréal, Canada | Rennaï'}
            </title>
            <meta name="title" content={parsedTitle} />
            <meta name="description" content={parsedDescription} />

            <meta property="og:url" content="https://www.rennai.com" />
            <meta property="og:type" content="website" />
            <meta property="og:title" content={parsedTitle} />
            <meta property="og:description" content={parsedDescription} />
            <meta
                property="og:image"
                content="https://images.ctfassets.net/v0rdkyiyx25c/2owwXvAfOLlnQnGWP8Amg4/f8d2e23924bc3f3beab99bed49d6df16/Rennai_Logo1.png"
            />

            <meta name="twitter:card" content="summary_large_image" />
            <meta property="twitter:domain" content="www.rennai.com" />
            <meta property="twitter:url" content="https://www.rennai.com" />
            <meta
                name="twitter:title"
                content="Rennaï: Your Self-Care & Beauty Boutique in Montréal, Canada | Rennaï"
            />
            <meta
                name="twitter:description"
                content="Explore Rennaï, your premier destination for self-care and beauty products in Montréal. Discover a curated selection of luxury skincare, wellness, and more."
            />
            <meta
                name="twitter:image"
                content="https://images.ctfassets.net/v0rdkyiyx25c/2owwXvAfOLlnQnGWP8Amg4/f8d2e23924bc3f3beab99bed49d6df16/Rennai_Logo1.png"
            ></meta>
            {noIndex && <meta name="robots" content="noindex" />}
            {children}
        </Helmet>
    )
}

Seo.propTypes = {
    replaceTitle: PropTypes.bool,
    title: PropTypes.string,
    description: PropTypes.string,
    noIndex: PropTypes.bool,
    children: PropTypes.node
}

export default Seo
